import {
  ForwardedRef,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useBlocker } from "react-router-dom";
import NavigationStepperCst from "@components/NavigationStepperCst";
import { WizardCstProps, WizardHandle } from "./types";
import { useWizardState } from "./hooks/useWizardState";
import { WizardContainer, WizardStepperRow, WizardContent } from "./style";
import WizardHeader from "./components/WizardHeader";
import WizardFooter from "./components/WizardFooter";
import WizardResetDialog from "./components/WizardResetDialog";
import WizardAbandonDialog from "./components/WizardAbandonDialog";

function WizardCstInner<TData>(
  {
    title,
    breadcrumb,
    backLink,
    headerAction,
    renderHeaderBadge,
    steps,
    initialData,
    onComplete,
    resetConfirm,
    abandonMode,
    abandonCopy,
    nextLabel = "Prosegui",
    resetLabel = "Reset",
    completeLabel = "Crea",
    backLabel = "Indietro",
    secondaryAction,
    isCompleting,
  }: WizardCstProps<TData>,
  ref: ForwardedRef<WizardHandle<TData>>,
) {
  const {
    activeStep,
    data,
    setData,
    errors,
    tryNext,
    goBack,
    goToStep,
    reset,
    isFirstStep,
    isLastStep,
    isDirty,
  } = useWizardState({ steps, initialData });

  const [isPersisted, setIsPersisted] = useState(false);
  const isPersistedRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const didMountRef = useRef(false);

  const shouldBlock = !!abandonMode && isDirty && !isPersisted;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isPersistedRef.current &&
      shouldBlock &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false);

  useEffect(() => {
    if (blocker.state === "blocked") setAbandonDialogOpen(true);
  }, [blocker.state]);

  useImperativeHandle(ref, () => ({ setData }), [setData]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
    let el = containerRef.current?.parentElement ?? null;
    while (el) {
      if (el.scrollTop > 0) el.scrollTop = 0;
      el = el.parentElement;
    }
  }, [activeStep]);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleNext = async () => {
    if (isCompleting) return;
    if (isLastStep) {
      const last = steps[activeStep];
      if (last?.validate) {
        const result = last.validate(data);
        if (!result.valid) {
          tryNext();
          return;
        }
      }
      try {
        isPersistedRef.current = true;
        setIsPersisted(true);
        await onComplete(data);
      } catch {
        isPersistedRef.current = false;
        setIsPersisted(false);
      }
      return;
    }
    tryNext();
  };

  const handleResetClick = () => {
    if (!resetConfirm) {
      reset();
      return;
    }
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    reset();
  };

  const runSecondaryAction = async () => {
    if (!secondaryAction) return;
    isPersistedRef.current = true;
    try {
      await secondaryAction.onClick(data);
      setIsPersisted(true);
    } catch {
      isPersistedRef.current = false;
    }
  };

  const ActiveStepComponent = steps[activeStep]?.Component;
  const stepperSteps = steps.map((s) => ({ key: s.key, label: s.label }));

  return (
    <WizardContainer ref={containerRef}>
      <WizardHeader
        title={title}
        breadcrumb={breadcrumb}
        backLink={backLink}
        headerAction={headerAction}
        titleBadge={renderHeaderBadge?.(data)}
      />

      <WizardStepperRow>
        <NavigationStepperCst
          steps={stepperSteps}
          activeStep={activeStep}
          onStepChange={goToStep}
          allowBackNavigation
        />
      </WizardStepperRow>

      <WizardContent>
        {ActiveStepComponent && (
          <ActiveStepComponent
            data={data}
            setData={setData}
            errors={errors}
            mode={abandonMode}
          />
        )}
      </WizardContent>

      <WizardFooter
        resetLabel={resetLabel}
        nextLabel={isLastStep ? completeLabel : nextLabel}
        backLabel={backLabel}
        onReset={handleResetClick}
        onNext={handleNext}
        onBack={goBack}
        showBack={!isFirstStep}
        secondaryAction={
          secondaryAction
            ? {
                label: secondaryAction.label,
                onClick: () => void runSecondaryAction(),
                disabled: secondaryAction.disabled,
              }
            : undefined
        }
        isCompleting={isCompleting}
      />

      {resetConfirm && (
        <WizardResetDialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          onConfirm={handleResetConfirm}
          copy={resetConfirm}
        />
      )}

      {abandonMode && (
        <WizardAbandonDialog
          open={abandonDialogOpen}
          mode={abandonMode}
          copy={abandonCopy}
          onClose={() => {
            setAbandonDialogOpen(false);
            if (blocker.state === "blocked") blocker.reset();
          }}
          onAbandon={() => {
            isPersistedRef.current = true;
            setAbandonDialogOpen(false);
            if (blocker.state === "blocked") blocker.proceed();
          }}
          onSaveDraft={
            secondaryAction
              ? () => {
                  setAbandonDialogOpen(false);
                  if (blocker.state === "blocked") blocker.reset();
                  void runSecondaryAction();
                }
              : undefined
          }
        />
      )}
    </WizardContainer>
  );
}

const WizardCst = forwardRef(WizardCstInner) as <TData>(
  props: WizardCstProps<TData> & { ref?: Ref<WizardHandle<TData>> },
) => ReturnType<typeof WizardCstInner>;

export default WizardCst;
